import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';
import type { HipaaSourceCandidate, HipaaAssemblyAssociation } from '../../functions/src/modules/hipaa/hipaaAssembly.shared';

export interface AssemblyPreviewSelection {
  firmId: string; clientId: string; providerId: string; firmFacilityId: string; versionId: string; documentId: string;
  expectedStoragePath: string; expectedSourceRevision: number; expectedAssociationRevision: number;
  expectedSourceGeneration: string;
  expectedPolicyVersion: number; expectedLifecycleRevision: number; expectedTemplateHash: string;
}
export interface AssemblyPreviewResult {
  authorizationStatus: 'preview'; pdfBase64: string;
  pageManifest: Array<{ role: 'reusable' | 'signed'; sourcePage: number }>;
  sourceSha256: string; sourceGeneration: string; versionHash: string; versionLifecycleRevision: number;
}
export async function previewHipaaAssembly(input: AssemblyPreviewSelection): Promise<AssemblyPreviewResult> {
  const result = await httpsCallable<AssemblyPreviewSelection, AssemblyPreviewResult>(functions, 'previewHipaaAssembly', { timeout: 180_000 })(input);
  if (result.data.authorizationStatus !== 'preview' || !Array.isArray(result.data.pageManifest)) throw new Error('The proposed preview could not be verified.');
  return result.data;
}

/** Bind the displayed original selection to the exact Storage object generation. */
export async function protectedHipaaPdfGeneration(storagePath: string): Promise<string> {
  const [{ getMetadata, ref }, { storage }] = await Promise.all([import('firebase/storage'), import('@/config/firebase')]);
  const generation = (await getMetadata(ref(storage, storagePath))).generation;
  if (!generation) throw new Error('The selected original changed. Refresh the authorization and try again.');
  return generation;
}

export async function prepareHipaaSource(input: {
  firmId: string; clientId: string; storagePath: string; documentId?: string; versionId?: string;
  expectedSourceRevision: number; expectedAssociationRevision: number;
}): Promise<HipaaSourceCandidate> {
  const result = await httpsCallable<typeof input, { candidate: HipaaSourceCandidate }>(functions, 'prepareHipaaSource')(input);
  return result.data.candidate;
}
export async function confirmHipaaAssociation(input: {
  firmId: string; clientId: string; documentId: string; sha256: string; objectGeneration: string; versionId: string;
  expectedSourceRevision: number; expectedAssociationRevision: number; expectedPolicyVersion: number; expectedLifecycleRevision: number; confirmed: boolean;
}): Promise<HipaaAssemblyAssociation> {
  const result = await httpsCallable<typeof input, { association: HipaaAssemblyAssociation }>(functions, 'confirmHipaaAssociation')(input);
  return result.data.association;
}

export async function prepareHipaaAuthorization(input: { firmId: string; clientId: string; providerId: string; firmFacilityId?: string }) {
  const result = await httpsCallable<typeof input, { success: boolean; url: string; documentId: string; storagePath: string; receiptId: string; filename: string }>(functions, 'prepareHipaaAuthorization')(input);
  return result.data;
}

/** Protected objects have no public download token. Read them with the current authenticated Storage session. */
export async function protectedHipaaPdfUrl(storagePath: string): Promise<string> {
  const [{ getBlob, ref }, { storage }] = await Promise.all([import('firebase/storage'), import('@/config/firebase')]);
  const blob = await getBlob(ref(storage, storagePath), 10 * 1024 * 1024);
  const url = URL.createObjectURL(blob.slice(0, blob.size, 'application/pdf'));
  setTimeout(() => URL.revokeObjectURL(url), 15 * 60 * 1000);
  return url;
}
export async function viewProtectedHipaaPdf(storagePath: string): Promise<void> {
  const viewer = globalThis.window?.open('', '_blank');
  try { const url = await protectedHipaaPdfUrl(storagePath); if (viewer) viewer.location.href = url; }
  catch (error) { viewer?.close(); throw error; }
}
